/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-argument */
import _ from 'lodash';
import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import type { AxiosError, AxiosResponse } from 'axios';

import { ltLuxon } from '../../helpers/ltLuxon';
import {
	CookieConstant,
	EmployeeProfileModuleConstant,
	EmployeeStateConstant,
	MessageConstant,
	StorageConstant
} from '../../constants';
import { employeeProfileService } from '../../services/employeeProfileService';
import { employeeStateService } from '../../services/employeeStateService';
import { sessionService, storageService } from '@life-time-inc/enterprise-framework-core';
import { getAuthZoneID } from '../../helpers/tmZone';
import { parseCookie } from '../../helpers/ltCookies';
import type { ltFramework, ltCommon } from '@life-time-inc/enterprise-framework-core';

// Diff from EF Core Types
interface IEmployeeProfileState {
	employeeProfile: IEmployeeProfile;
}

// TODO: Reconsile roles with EF Core
interface IEmployeeProfile {
	sso: string;
	partyId: null | number;
	employeeDetails: null | ltFramework.IEmployeeDetails;
	employeeState: string;
	roles: null | ltFramework.IRoleGroup[];
	initialized: boolean;
	verified: boolean;
	authentication: string;
	jwt: null | string;
}

const EmployeeProfileState: IEmployeeProfileState = {
	employeeProfile: {
		employeeState: EmployeeStateConstant.unknown,
		initialized: false,
		verified: false
	} as IEmployeeProfile
};

const { action, mutation } = EmployeeProfileModuleConstant;

const EmployeeProfileMutations: MutationTree<IEmployeeProfileState> = {
	[mutation.setEmployeeProfile](state: IEmployeeProfileState, payload: IEmployeeProfile) {
		state.employeeProfile = payload;

		// update profile-dependent elements on page
		$('[data-profile]').each((index, item) => {
			const element = $(item);
			const profileElement = element.data('profile-property');
			if (profileElement) {
				const property = _.result(payload, profileElement);
				if (property) {
					element.html(
						(element.data('profile-prefix') ?? '')
						+ property
						+ (element.data('profile-suffix') ?? '')
					);
				}
			}
		});
	}
};

const EmployeeProfileActions: ActionTree<IEmployeeProfileState, ltFramework.IRootState> = {
	[action.initialize]({
		commit
	}: ActionContext<IEmployeeProfileState, ltFramework.IRootState>): Promise<void> {
		const zoneId = getAuthZoneID();
		let sso = sessionService.getCookie(CookieConstant.tmSsoId, zoneId);
		const aemToken: string = sessionService.getCookie(CookieConstant.aemToken, zoneId);
		const authentication: string = sessionService.getCookie(
			CookieConstant.ltAuthentication,
			zoneId
		);
		const roleGroups = parseCookie(CookieConstant.roles, [], zoneId) as ltFramework.IRoleGroup[];
		let employeeState = employeeStateService.getEmployeeState(sso, zoneId);

		const partyId = storageService.sessionGet(StorageConstant.partyId, zoneId) as number;
		const employeeDetails = parseCookie(
			CookieConstant.employeeDetails,
			null,
			zoneId
		) as ltFramework.IEmployeeDetails | null;
		if (employeeDetails) {
			const storageEmployeeProfile: IEmployeeProfile = {} as IEmployeeProfile;
			storageEmployeeProfile.sso = sso;
			storageEmployeeProfile.partyId = partyId;
			storageEmployeeProfile.authentication = authentication;
			storageEmployeeProfile.roles = roleGroups;
			storageEmployeeProfile.employeeDetails = employeeDetails;
			storageEmployeeProfile.employeeState = employeeState;
			storageEmployeeProfile.initialized = employeeDetails.employeeId > 0;
			storageEmployeeProfile.partyId = employeeDetails.partyId;

			return new Promise<void>((resolve) => {
				commit(mutation.setEmployeeProfile, storageEmployeeProfile);
				resolve();
			});
		}

		if (!sso) {
			return new Promise<void>((resolve) => {
				const errorEmployeeProfile: IEmployeeProfile = {} as IEmployeeProfile;
				errorEmployeeProfile.sso = sso;
				errorEmployeeProfile.partyId = null;
				errorEmployeeProfile.employeeDetails = null;
				errorEmployeeProfile.employeeState = EmployeeStateConstant.unauthenticated;
				errorEmployeeProfile.roles = null;
				errorEmployeeProfile.initialized = true;
				errorEmployeeProfile.verified = true;

				commit(mutation.setEmployeeProfile, errorEmployeeProfile);

				resolve();
			});
		}

		return employeeProfileService.getEmployeeProfile().then(
			(response: AxiosResponse<ltFramework.IEmployeeProfileResponse>) => {
				employeeState = employeeStateService.getEmployeeState(sso, zoneId);

				const responseEmployeeProfile: IEmployeeProfile = {} as IEmployeeProfile;
				responseEmployeeProfile.sso = sso;
				responseEmployeeProfile.partyId = response.data.partyId;
				responseEmployeeProfile.authentication = authentication;
				responseEmployeeProfile.employeeDetails = {
					employeeId: response.data.employeeId,
					partyId: response.data.partyId,
					memberId: response.data.memberId,
					firstName: response.data.firstName,
					lastName: response.data.lastName,
					title: response.data.title,
					salutation: response.data.salutation,
					preferredFirstName: response.data.preferredFirstName,
					preferredLastName: response.data.preferredLastName,
					terminationDate: response.data.terminationDate
						? ltLuxon(response.data.terminationDate).toUTC().toISO()
						: null,
					employeeActiveFlag: response.data.employeeActiveFlag,
					primaryWorkEmail: response.data.primaryWorkEmail,
					phoneNumber: response.data.phoneNumber,
					primaryClubId: response.data.primaryClubId
				} as ltFramework.IEmployeeDetails;
				responseEmployeeProfile.employeeState = employeeState;
				responseEmployeeProfile.initialized = true;
				responseEmployeeProfile.verified = true;

				storageService.sessionSet(StorageConstant.partyId, responseEmployeeProfile.partyId, zoneId);
				sessionService.setCookie(
					CookieConstant.employeeDetails,
					JSON.stringify(responseEmployeeProfile.employeeDetails),
					CookieConstant.ssoIdExpires,
					false,
					zoneId
				);

				if (responseEmployeeProfile.sso) {
					sessionService.setCookie(
						CookieConstant.tmSsoId,
						responseEmployeeProfile.sso,
						CookieConstant.ssoIdExpires,
						false,
						zoneId
					);
				}

				if (aemToken) {
					sessionService.setCookie(
						CookieConstant.aemToken,
						aemToken,
						CookieConstant.aemTokenExpires,
						false,
						zoneId
					);
				}

				if (roleGroups) {
					sessionService.setCookie(
						CookieConstant.roles,
						JSON.stringify(roleGroups),
						CookieConstant.rolesExpires,
						false,
						zoneId
					);
					responseEmployeeProfile.roles = roleGroups;
				}

				commit(mutation.setEmployeeProfile, responseEmployeeProfile);
			},
			(response: AxiosError | Error) => {
				const axiosError: AxiosError = response as AxiosError;
				if (axiosError?.response) {
					switch (axiosError.response.status) {
						case 401:
						case 502:
							sso = '';
							sessionService.invalidateAuthSession(zoneId);
							break;
						default:
							break;
					}
				}

				const error: Error = response as Error;
				if (error?.message) {
					switch (error.message) {
						case MessageConstant.expiredSsoMessage:
						case MessageConstant.timeout:
							sso = '';
							sessionService.invalidateAuthSession(zoneId);
							break;
						case MessageConstant.invalidRequestMessage:
						case MessageConstant.profileNotFoundMessage:
							sso = '';
							sessionService.invalidateSession(zoneId);
							break;
						default:
							break;
					}
				}

				employeeState = employeeStateService.getEmployeeState(sso, zoneId);

				const errorEmployeeProfile: IEmployeeProfile = {} as IEmployeeProfile;
				errorEmployeeProfile.sso = sso;
				errorEmployeeProfile.partyId = null;
				errorEmployeeProfile.employeeDetails = null;
				errorEmployeeProfile.employeeState = employeeState;
				errorEmployeeProfile.roles = null;
				errorEmployeeProfile.initialized = true;
				errorEmployeeProfile.verified = true;

				commit(mutation.setEmployeeProfile, errorEmployeeProfile);
			}
		);
	},

	[action.logout](): Promise<AxiosResponse<ltCommon.ILogoutResponse> | void> {
		const zoneId = getAuthZoneID();
		sessionService.invalidateSession(zoneId);

		return Promise.resolve();
	}
};

export const EmployeeProfileModule: Module<IEmployeeProfileState, ltFramework.IRootState> = {
	namespaced: true,
	state: EmployeeProfileState,
	actions: EmployeeProfileActions,
	mutations: EmployeeProfileMutations
};
